'use client';

import React, { useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Tag } from '@/components/ui';
import { useDeviceType } from '@/hooks/useDeviceType';
import { InfoPopover } from '@/components/ui/InfoPopover';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { HIGHLIGHT_DESCRIPTIONS, isTopRated, type TagVariant } from '@repo/utils';
import type { Listing } from '@repo/api';
import {
  IconVerified,
  IconTopRated,
  IconRentStabilized,
  IconGoodCause,
  IconFuture,
  IconWorst,
} from '@repo/svgs';

export interface ListingCardTagProps {
  /** The listing data to derive tags from */
  listing: Listing;
  /** Custom className for the container */
  className?: string;
}

const ICON_BY_VARIANT: Record<TagVariant, React.ComponentType<any>> = {
  verified: IconVerified,
  topRated: IconTopRated,
  rentStabilized: IconRentStabilized,
  goodCause: IconGoodCause,
  future: IconFuture,
  worstLandlord: IconWorst,
};

function TagHighlightsContent({ tags }: { tags: TagVariant[] }) {
  return (
    <div className={cn('flex flex-col gap-1')}>
      {tags.map((variant, index) => {
        const highlight = HIGHLIGHT_DESCRIPTIONS[variant];
        const IconComponent = ICON_BY_VARIANT[variant];

        if (!highlight || !IconComponent) return null;

        return (
          <div key={variant}>
            <div className={cn('flex gap-4 items-start px-2 py-3')}>
              <div className={cn('w-6 h-6 flex-shrink-0')}>
                <IconComponent className="w-full h-full" />
              </div>

              <div className={cn('flex-1 flex flex-col gap-1')}>
                <p className={cn('text-body-2-high text-primary')}>{highlight.title}</p>
                <p className={cn('text-caption text-secondary')}>{highlight.description}</p>
              </div>
            </div>

            {index < tags.length - 1 && <div className={cn('h-px bg-tertiary mx-2')} />}
          </div>
        );
      })}
    </div>
  );
}

export function ListingCardTag({ listing, className }: ListingCardTagProps) {
  const { isMobile } = useDeviceType();
  const [showTagDetails, setShowTagDetails] = useState(false);
  const tagContainerRef = useRef<HTMLDivElement | null>(null);

  const hasTopRated = isTopRated(listing.buildingAverageRating, listing.buildingReviewCount);

  // Derive tags from listing data
  const tags: TagVariant[] = [
    listing.activeListing?.isFutureListing && 'future',
    listing.isBuildingVerified && 'verified',
    hasTopRated && 'topRated',
    listing.isBuildingRentStabilized && 'rentStabilized',
    listing.hasBuildingGoodCauseEviction && 'goodCause',
  ].filter(Boolean) as TagVariant[];

  // Show only first 2 tags as per design
  const displayTags = tags.slice(0, 2);

  const handleTagClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation to listing page
    e.stopPropagation(); // Prevent event bubbling
    setShowTagDetails(true);
  };

  const handleCloseTagDetails = () => {
    setShowTagDetails(false);
  };

  // Don't render anything if no tags
  if (displayTags.length === 0) {
    return null;
  }

  return (
    <>
      <div
        ref={tagContainerRef}
        className={cn(
          // Absolute positioning
          // Ensure tags render above carousel/images (iOS Safari can mis-paint without an explicit stacking context)
          'absolute top-3 left-3 z-30',
          // Flex row, gaps & wrap
          'flex flex-wrap gap-2',
          // Width constraint
          'max-w-[calc(100%-6rem)]',
          className,
        )}
      >
        {displayTags.map(tagVariant => (
          <div key={tagVariant} onClick={handleTagClick}>
            <Tag variant={tagVariant} />
          </div>
        ))}
      </div>

      {/* Tag Details Modal/Bottom Sheet */}
      {tags.length > 0 && (
        <>
          {isMobile ? (
            <BottomSheet
              open={showTagDetails}
              onOpenChange={setShowTagDetails}
              label="Listing highlights"
              onClose={handleCloseTagDetails}
              bodyClassName="!px-4 !py-2"
            >
              <TagHighlightsContent tags={tags} />
            </BottomSheet>
          ) : (
            // Desktop InfoPopover
            <InfoPopover
              open={showTagDetails}
              onOpenChange={isOpen => !isOpen && handleCloseTagDetails()}
              onClose={handleCloseTagDetails}
              label="Listing highlights"
              {...(tagContainerRef && { anchorRef: tagContainerRef })}
              placement="below"
              align="center"
              offsetY={8}
              bodyClassName="px-4 py-2"
            >
              <TagHighlightsContent tags={tags} />
            </InfoPopover>
          )}
        </>
      )}
    </>
  );
}

export default ListingCardTag;
